import { forwardRef } from 'react'

import styles from './CartItem.module.css'

import type { CartItem as CartItemType } from '@checkout/core'

export interface CartItemProps {
  /** Cart item data */
  item: CartItemType
  /** Whether to show product image */
  showImage?: boolean
  /** Whether to show quantity controls (+/-) */
  showQuantityControls?: boolean
  /** Whether to show remove button */
  showRemoveButton?: boolean
  /** Currency formatting function */
  formatCurrency: (amount: number, currency: string) => string
  /** Quantity change handler */
  onQuantityChange?: (itemId: string, quantity: number) => void
  /** Remove item handler */
  onRemove?: (itemId: string) => void
  /** Whether this is the last item (no divider) */
  isLast?: boolean
  /** Additional class name */
  className?: string
}

export const CartItem = forwardRef<HTMLDivElement, CartItemProps>(
  (
    {
      item,
      showImage = false,
      showQuantityControls = false,
      showRemoveButton = false,
      formatCurrency,
      onQuantityChange,
      onRemove,
      isLast = false,
      className,
    },
    ref
  ) => {
    const totalPrice = item.unitPrice * item.quantity
    const hasOriginalPrice = item.originalPrice && item.originalPrice > item.unitPrice

    const handleDecrease = () => {
      if (item.quantity > 1) {
        onQuantityChange?.(item.id, item.quantity - 1)
      }
    }

    const handleIncrease = () => {
      onQuantityChange?.(item.id, item.quantity + 1)
    }

    const handleRemove = () => {
      onRemove?.(item.id)
    }

    return (
      <div
        ref={ref}
        className={`${styles.container} ${className ?? ''}`}
        data-ga-module="order_summary"
      >
        {/* Product image with quantity badge */}
        {showImage && item.image && (
          <div className={styles.imageWrapper}>
            <img src={item.image} alt={item.name} className={styles.image} />
            {item.quantity > 1 && <span className={styles.badge}>{item.quantity}</span>}
          </div>
        )}

        {/* Content: two-row layout */}
        <div className={styles.content}>
          {/* First row: name + price */}
          <div className={styles.firstRow}>
            <span className={styles.name}>{item.name}</span>
            <span className={styles.price}>{formatCurrency(totalPrice, item.currency)}</span>
          </div>

          {/* Second row: description (or name as fallback) + original price */}
          <div className={styles.secondRow}>
            <span className={styles.description}>{item.description || item.name}</span>
            {hasOriginalPrice && (
              <span className={styles.originalPrice}>
                {formatCurrency(item.originalPrice! * item.quantity, item.currency)}
              </span>
            )}
          </div>

          {/* Quantity controls (optional) */}
          {showQuantityControls && (
            <div className={styles.controls}>
              <button
                type="button"
                className={styles.controlButton}
                onClick={handleDecrease}
                disabled={item.quantity <= 1}
                aria-label="Decrease quantity"
              >
                −
              </button>
              <span className={styles.quantity}>{item.quantity}</span>
              <button
                type="button"
                className={styles.controlButton}
                onClick={handleIncrease}
                aria-label="Increase quantity"
              >
                +
              </button>
              {showRemoveButton && (
                <button
                  type="button"
                  className={styles.removeButton}
                  onClick={handleRemove}
                  aria-label="Remove item"
                >
                  Remove
                </button>
              )}
            </div>
          )}
        </div>

        {/* Divider (except for last item) */}
        {!isLast && <div className={styles.divider} />}
      </div>
    )
  }
)

CartItem.displayName = 'CartItem'
